/*
 * DrillLogPro persistence layer.
 * Native Android/iOS: @capacitor-community/sqlite.
 * Browser/PWA fallback: IndexedDB.
 *
 * The application code talks to this module through a small key/value API,
 * so switching from Base44 to local persistence does not require every page
 * to know how the database is implemented.
 */
import { Capacitor } from '@capacitor/core';

const STORE_NAMES = [
  'Sheet',
  'DrillRun',
  'DepthAlert',
  'Stocktake',
  'User',
  'AuthState',
  'AccountDeletionRequest',
  'AccountRecoveryRequest',
  'File',
  'SyncQueue',
  'AuditLog',
  'DailyLog',
  'DailyTask',
  'FeaturePermission',
  'DocumentTemplate',
  'AirCoreRun',
  'AirCoreSample',
  'RCRun',
  'ShiftSnapshot',
];

const DB_NAME = 'drilllogpro';
const DB_VERSION = 2;
let nativeDbPromise;
let webDbPromise;

function openWebDb() {
  if (webDbPromise) return webDbPromise;
  webDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open('DrillLogPro', DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const name of STORE_NAMES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return webDbPromise;
}

function webTable(name) {
  const run = (mode, action) => openWebDb().then((database) => new Promise((resolve, reject) => {
    const tx = database.transaction(name, mode);
    const request = action(tx.objectStore(name));
    if (!request) {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      return;
    }
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }));

  return {
    get: (id) => run('readonly', (store) => store.get(id)),
    put: (value) => run('readwrite', (store) => store.put(value)).then(() => value),
    add: (value) => run('readwrite', (store) => store.add(value)).then(() => value),
    delete: (id) => run('readwrite', (store) => store.delete(id)),
    all: () => run('readonly', (store) => store.getAll()).then((v) => v || []),
    bulkPut: async (values) => {
      const database = await openWebDb();
      await new Promise((resolve, reject) => {
        const tx = database.transaction(name, 'readwrite');
        const store = tx.objectStore(name);
        values.forEach((value) => store.put(value));
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
      return values;
    },
    bulkDelete: async (ids) => {
      const database = await openWebDb();
      await new Promise((resolve, reject) => {
        const tx = database.transaction(name, 'readwrite');
        const store = tx.objectStore(name);
        ids.forEach((id) => store.delete(id));
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

async function openNativeDb() {
  if (nativeDbPromise) return nativeDbPromise;
  nativeDbPromise = (async () => {
    const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
    const connection = new SQLiteConnection(CapacitorSQLite);
    const db = await connection.createConnection(DB_NAME, false, 'no-encryption', 1);
    await db.open();
    await db.execute(`
      CREATE TABLE IF NOT EXISTS records (
        entity TEXT NOT NULL,
        id TEXT NOT NULL,
        payload TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        PRIMARY KEY (entity, id)
      );
    `);
    return db;
  })();
  return nativeDbPromise;
}

function nativeTable(name) {
  return {
    async get(id) {
      const db = await openNativeDb();
      const result = await db.query('SELECT payload FROM records WHERE entity = ? AND id = ?', [name, id]);
      return result.values?.[0] ? JSON.parse(result.values[0].payload) : undefined;
    },
    async put(value) {
      const db = await openNativeDb();
      await db.run(
        'INSERT OR REPLACE INTO records(entity,id,payload,updated_at) VALUES(?,?,?,?)',
        [name, value.id, JSON.stringify(value), new Date().toISOString()],
      );
      return value;
    },
    async add(value) {
      const db = await openNativeDb();
      await db.run(
        'INSERT INTO records(entity,id,payload,updated_at) VALUES(?,?,?,?)',
        [name, value.id, JSON.stringify(value), new Date().toISOString()],
      );
      return value;
    },
    async delete(id) {
      const db = await openNativeDb();
      await db.run('DELETE FROM records WHERE entity = ? AND id = ?', [name, id]);
    },
    async all() {
      const db = await openNativeDb();
      const result = await db.query('SELECT payload FROM records WHERE entity = ?', [name]);
      return (result.values || []).map((row) => JSON.parse(row.payload));
    },
    async bulkPut(values) {
      for (const value of values) await this.put(value);
      return values;
    },
    async bulkDelete(ids) {
      for (const id of ids) await this.delete(id);
    },
  };
}

export const isNative = Capacitor.isNativePlatform();
export const store = (name) => (isNative ? nativeTable(name) : webTable(name));
